/**
 * Feature pricing configuration grid — per-account pricing (default
 * price + site group/site overrides) for each billable software
 * feature. Per-device features additionally carry a device type
 * (camera/radio); customer-scoped-only features can't take overrides
 * at all.
 */
import { FormControl, MenuItem, Select, TextField, Tooltip, Typography } from '@mui/material';
import React from 'react';

import * as BillingApi from '@sophocles/api/billing';
import { loadFeaturePricingConfig, saveFeaturePricingConfig } from '@sophocles/features/billing/api';
import {
  buildActionsColumn,
  buildOverridesColumn,
  EnumCellDisplay,
} from '@sophocles/views/billing/components/pricingConfigColumns';
import PricingConfigGrid from '@sophocles/views/billing/components/PricingConfigGrid';

function getPricingUnits(t) {
  return [
    { value: 'FLAT', label: t('billing.pricingConfig.pricingUnitFlat') },
    { value: 'PER_SITE', label: t('billing.pricingConfig.pricingUnitPerSite') },
    { value: 'PER_SITE_GROUP', label: t('billing.pricingConfig.pricingUnitPerSiteGroup') },
    { value: 'PER_DEVICE', label: t('billing.pricingConfig.pricingUnitPerDevice') },
    { value: 'PER_USER', label: t('billing.pricingConfig.pricingUnitPerUser') },
  ];
}

function getDeviceTypes(t) {
  return [
    { value: 'CAMERA', label: t('billing.pricingConfig.deviceTypeCamera') },
    { value: 'RADIO', label: t('billing.pricingConfig.deviceTypeRadio') },
  ];
}

// A feature that only supports the customer entity type (no site group
// or site scoping) can't take overrides at all.
function isCustomerOnlyFeature(row) {
  const supportedEntityTypes = row.feature?.supported_entity_types || [];
  return (
    supportedEntityTypes.length > 0 &&
    supportedEntityTypes.includes('customer') &&
    !supportedEntityTypes.includes('sitegroup') &&
    !supportedEntityTypes.includes('site')
  );
}

const featureDescriptor = {
  configType: 'feature',
  getRowKey: config => config.feature?.key,
  entityLabel: config => config.feature?.name,
  fetchDefaultTemplate: () => BillingApi.fetchDefaultFeaturePricingTemplate(),
  loadTemplateFailedLogKey: 'billing.pricingConfig.loadFeatureTemplateFailedLog',
  loadTemplateFailedMessageKey: 'billing.pricingConfig.loadFeatureTemplateFailed',
  loadAction: loadFeaturePricingConfig,
  saveAction: saveFeaturePricingConfig,
  buildSaveConfigEntry: c => ({
    feature_key: c.feature?.key,
    pricing_unit: c.pricing_unit,
    device_type: c.device_type,
    default_price: c.default_price,
    overrides: (c.overrides || []).map(o => ({
      site_group_id: o.site_group?.id || null,
      site_id: o.site?.id || null,
      price: o.price,
    })),
  }),
  buildColumns: ({ t, editMode, alwaysEditMode, onUpdateConfig, onOpenOverrides, onActionMenuOpen }) => {
    const pricingUnits = getPricingUnits(t);
    const deviceTypes = getDeviceTypes(t);

    return [
      {
        field: 'category',
        headerName: t('billing.pricingConfig.columnCategory'),
        width: 150,
        valueGetter: (value, row) => row.feature?.category?.name || t('billing.pricingConfig.uncategorized'),
      },
      {
        field: 'featureName',
        headerName: t('billing.pricingConfig.columnFeature'),
        flex: 1,
        minWidth: 180,
        valueGetter: (value, row) => row.feature?.name || '',
      },
      {
        field: 'pricing_unit',
        headerName: t('billing.pricingConfig.columnPricingUnit'),
        width: 180,
        renderCell: params =>
          editMode ? (
            <FormControl size="small" fullWidth hiddenLabel sx={{ height: '100%', justifyContent: 'center' }}>
              <Select
                value={params.value || ''}
                onChange={e =>
                  onUpdateConfig(params.row.feature?.key, {
                    pricing_unit: e.target.value || null,
                    device_type: e.target.value === 'PER_DEVICE' ? 'CAMERA' : null,
                    ...(e.target.value ? {} : { default_price: null }),
                    ...(e.target.value === 'FLAT' ? { overrides: [] } : {}),
                  })
                }
                displayEmpty
                onClick={e => e.stopPropagation()}
              >
                <MenuItem value="">
                  <em>{t('billing.pricingConfig.notConfigured')}</em>
                </MenuItem>
                {pricingUnits.map(unit => (
                  <MenuItem key={unit.value} value={unit.value}>
                    {unit.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <EnumCellDisplay
              label={
                pricingUnits.find(u => u.value === params.value)?.label || (
                  <span style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                    {t('billing.pricingConfig.notConfigured')}
                  </span>
                )
              }
            />
          ),
      },
      {
        field: 'device_type',
        headerName: t('billing.pricingConfig.columnDeviceType'),
        width: 140,
        renderCell: params => {
          const isPricePerDevice = params.row.pricing_unit === 'PER_DEVICE';
          if (!isPricePerDevice) {
            return (
              <EnumCellDisplay
                label={
                  <Typography variant="body2" color="text.disabled">
                    —
                  </Typography>
                }
              />
            );
          }
          return editMode ? (
            <FormControl size="small" fullWidth hiddenLabel sx={{ height: '100%', justifyContent: 'center' }}>
              <Select
                value={params.value || 'CAMERA'}
                onChange={e => onUpdateConfig(params.row.feature?.key, { device_type: e.target.value })}
                onClick={e => e.stopPropagation()}
              >
                {deviceTypes.map(type => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <EnumCellDisplay label={deviceTypes.find(dt => dt.value === params.value)?.label || ''} />
          );
        },
      },
      {
        field: 'default_price',
        headerName: t('billing.pricingConfig.columnDefaultPrice'),
        width: 140,
        renderCell: params =>
          editMode ? (
            <TextField
              size="small"
              // No label in this grid cell — hiddenLabel drops the padding
              // filled otherwise reserves for a floating label, so the
              // price text sits centered in the row instead of low.
              hiddenLabel
              type="number"
              value={params.value || ''}
              onChange={e => onUpdateConfig(params.row.feature?.key, { default_price: e.target.value || '0' })}
              onClick={e => e.stopPropagation()}
              InputProps={{
                startAdornment: <Typography sx={{ mr: 0.5, color: 'text.secondary' }}>$</Typography>,
              }}
              inputProps={{ step: '0.01', min: '0' }}
              disabled={!params.row.pricing_unit}
              sx={{ width: '100%' }}
            />
          ) : (
            <EnumCellDisplay
              label={
                params.value ? (
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    ${parseFloat(params.value).toFixed(2)}
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.disabled">
                    —
                  </Typography>
                )
              }
            />
          ),
      },
      // Overrides column - hidden in alwaysEditMode (inline forms don't support overrides)
      ...(!alwaysEditMode
        ? [
            buildOverridesColumn({
              t,
              onOpenOverrides,
              getOverridesGate: row => {
                const pricingUnit = row.pricing_unit;
                const customerOnly = isCustomerOnlyFeature(row);
                if (customerOnly) {
                  return { naButton: true, tooltip: t('billing.pricingConfig.categoryOnlyFeatureTooltip') };
                }
                return { disabled: !pricingUnit || customerOnly, tooltip: null };
              },
            }),
          ]
        : []),
      // Actions column - only visible in edit mode and not in alwaysEditMode (inline forms)
      ...(editMode && !alwaysEditMode ? [buildActionsColumn({ onActionMenuOpen })] : []),
    ];
  },
};

/**
 * FeaturePricingConfigGrid - Grid for configuring feature pricing
 *
 * Supports two modes:
 * 1. Redux mode (default): Pass accountId to load/save configs via Redux/API
 * 2. Controlled mode: Pass value/onChange to manage configs locally (for inline forms)
 *
 * @param {string} accountId - The billing account ID (required for Redux mode)
 * @param {string} customerId - Customer ID for autocompletes
 * @param {function} renderToolbar - Custom toolbar renderer
 * @param {Array} value - Controlled mode: array of config objects
 * @param {function} onChange - Controlled mode: callback when configs change
 * @param {boolean} alwaysEditMode - Always be in edit mode (for inline forms)
 */
function FeaturePricingConfigGrid(props) {
  return <PricingConfigGrid descriptor={featureDescriptor} {...props} />;
}

export default FeaturePricingConfigGrid;
